export type BookingStatus = "INITIATED" | "HELD" | "CONFIRMED" | "CANCELLED" | "EXPIRED" | "RESCHEDULED";

export type SubscriptionStatus =
  | "TRIAL"
  | "ACTIVE"
  | "PAST_DUE"
  | "CANCELLED"
  | "EXPIRED"
  | "REFUNDED"
  | "INCOMPLETE";

export interface SubscriptionStateDto {
  status: SubscriptionStatus | null;
  entitled: boolean;
  trialEnd: string | null;
  trialDaysLeft: number;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

/** Effective entitlement tier (mirrors backend PlanTier). */
export type PlanTier = "FREE" | "PROFESSIONAL" | "TEAM" | "ENTERPRISE";

/** Boolean, plan-controlled capabilities (mirror backend Feature enum names). */
export type Feature =
  | "GROUP_EVENT"
  | "ROUND_ROBIN_EVENT"
  | "COLLECTIVE_EVENT"
  | "TEAMS"
  | "BOOKING_FORMS"
  | "EXPERIENCES"
  | "CUSTOM_BRANDING"
  | "WHITE_LABEL";

/** Numeric, plan-controlled quotas (mirror backend LimitKey enum names). -1 = unlimited. */
export type LimitKey =
  | "CONNECTED_CALENDARS"
  | "MAX_EVENT_TYPES"
  | "MAX_TEAM_MEMBERS"
  | "MAX_TEAMS";

/**
 * Resolved entitlements from GET /api/me. The client reads these to mirror feature access;
 * it never derives the plan matrix itself (backend is the source of truth).
 */
export interface EntitlementsDto {
  tier: PlanTier;
  features: Feature[];
  limits: Partial<Record<LimitKey, number>>;
}

/** Sentinel matching backend LimitKey.UNLIMITED. */
export const UNLIMITED = -1;

export interface BillingPlanDto {
  code: string;
  name: string;
  description: string | null;
  amountMinor: number;
  currency: string;
  billingInterval: "MONTH" | "YEAR";
  trialDays: number;
}

export interface PurchasableBillingPlanDto extends BillingPlanDto {
  id: string;
  defaultPlan: boolean;
  /** Which tier buying this plan grants. Required to tell two paid plans apart. */
  tier: PlanTier;
  /**
   * Seats this plan allows, or null to use its tier's own value. Team-5 and Team-10 share a
   * tier and a billing interval, so this is the only thing that distinguishes them.
   */
  maxTeamMembers: number | null;
  /**
   * Extra features this plan grants beyond its tier, comma-separated. Professional and
   * Professional-with-white-label are the same tier at the same interval, so this is the only
   * thing that separates them.
   */
  grantedFeatures: string | null;
}

export interface BillingPaymentMethodDto {
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
}

export interface BillingOverviewDto {
  billingEnabled: boolean;
  /**
   * Whether in-place paid→paid plan change is available. When false the billing UI disables
   * Upgrade/Downgrade CTAs for active paid subscribers and shows the cancel-then-switch path.
   */
  planChangeEnabled: boolean;
  state: SubscriptionStateDto;
  plan: BillingPlanDto;
  paymentMethod: BillingPaymentMethodDto | null;
  /** An in-flight plan change to poll after requesting one; null when none is pending. */
  pendingPlanChange: {
    targetPlanCode: string;
    /** PENDING while awaiting the provider; FAILED if the prorated charge was declined. */
    status: "PENDING" | "FAILED";
  } | null;
}

export type AnnouncementLevel = "INFO" | "WARNING" | "CRITICAL";
export type AnnouncementAudience = "ALL" | "FREE" | "PAID";

export interface PublicAnnouncementDto {
  id: string;
  title: string | null;
  body: string;
  level: AnnouncementLevel;
  audience: AnnouncementAudience;
  startsAt: string | null;
  endsAt: string | null;
}

export interface DiscountBreakdownDto {
  baseMinor: number;
  couponMinor: number;
  manualMinor: number;
  taxMinor: number;
  totalMinor: number;
  currency: string;
  appliedPromoCode: string | null;
}

export type InvoiceStatus = "DRAFT" | "PAID" | "VOID" | "REFUNDED" | "PARTIALLY_REFUNDED";

export interface ReceiptDto {
  id: string;
  receiptNumber: string;
  status: InvoiceStatus;
  totalMinor: number;
  amountRefundedMinor: number;
  currency: string;
  periodStart: string | null;
  periodEnd: string | null;
  issuedAt: string;
  paymentId: string | null;
  subscription: string;
  nextRenewal: string | null;
  officialInvoiceNumber: string | null;
  officialInvoiceUrl: string | null;
}

export interface UserDto {
  id: string;
  email: string;
  username: string;
  name: string;
  timezone: string;
  profileImage: string | null;
  hasCustomAvatar: boolean;
  avatarVersion: number | null;
  /** Subscription/entitlement summary for client-side feature gating; null if billing omitted. */
  subscription?: SubscriptionStateDto | null;
  /** Resolved feature/limit entitlements (UX gating only); null if omitted. */
  entitlements?: EntitlementsDto | null;
}

export type OnboardingStatus = "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";
export type OnboardingUseCase = "CONSULTING" | "SALES_RECRUITING" | "TEAM_MANAGEMENT" | "PERSONAL";
export type OnboardingStep = "PURPOSE" | "AVAILABILITY" | "CALENDAR" | "FIRST_EVENT" | "SUCCESS";

export interface OnboardingStateResponse {
  version: number;
  status: OnboardingStatus;
  useCase: OnboardingUseCase | null;
  lastStep: OnboardingStep | null;
  resumeStep: OnboardingStep;
  availabilityConfirmed: boolean;
  availabilityReady: boolean;
  calendarReady: boolean;
  firstEventReady: boolean;
  missingRequirements: Array<"availability" | "calendar" | "firstEvent" | string>;
  /**
   * Derived convenience: `missingRequirements.length === 0`. Distinct from `status`, which records
   * history and is never rewritten — a user can be `status === "COMPLETED"` yet
   * `accountReady === false` after deleting all their availability. Route-entry gating keys off
   * this (per-route, via routeSetupPolicy), never off `status`.
   */
  accountReady: boolean;
  completedAt: string | null;
  /**
   * The team whose invitation brought this user in, or null if they arrived on their own. Non-null
   * makes the first-event step an offer rather than a requirement, and is the team to focus on the
   * roster once onboarding finishes.
   */
  invitedTeamId?: string | null;
  /** That team's name, for framing the steps. Null if the team has since been deleted. */
  invitedTeamName?: string | null;
}

export interface OnboardingUpdateRequest {
  useCase?: OnboardingUseCase;
  lastStep?: OnboardingStep;
  availabilityConfirmed?: boolean;
  deferred?: boolean;
}

/**
 * A host's stored branding for their public booking pages, plus whether their current plan can
 * actually use it. Fields are always the host's stored values regardless of plan -- a downgrade
 * must not discard what they saved, only stop it from being applied on public pages (the server
 * enforces that separately on read).
 *
 * Two capability flags, because they gate different controls. `canBrand` (CUSTOM_BRANDING,
 * Professional+) is whether the name/logo/colour/background fields are editable at all --
 * Settings renders the whole fieldset disabled with an upgrade prompt when it's false.
 * `canWhiteLabel` (WHITE_LABEL, Team or the Professional add-on) gates only the
 * hideBunnycalBranding checkbox inside it; a Professional host has the first and not the second.
 */
export interface HostBrandingDto {
  /** Replaces the "BunnyCal" wordmark on the host's public pages. Null shows ours. */
  brandName: string | null;
  brandColor: string | null;
  brandLogoUrl: string | null;
  /** null = unthemed (the default page), which is distinct from SOLID with no colour picked. */
  backgroundType: "SOLID" | "LINEAR_GRADIENT" | null;
  backgroundColor: string | null;
  gradientStartColor: string | null;
  gradientEndColor: string | null;
  gradientAngle: number | null;
  /**
   * Stored but not yet applied to the hosted page: the deriver would re-derive the whole text
   * ramp page-wide while the panels still carry their own ivory fills.
   */
  textColor: string | null;
  /**
   * The host's stored "hide the BunnyCal mark" choice -- independent of whether a name or logo
   * is set, and independent of canWhiteLabel: it survives a lapsed entitlement unchanged, the
   * same as every other field here, so an upgrade honours it again with nothing to re-enter.
   */
  hideBunnycalBranding: boolean;
  canBrand: boolean;
  canWhiteLabel: boolean;
}

export interface AvatarMetadata {
  avatarUrl: string | null;
  hasCustomAvatar: boolean;
  avatarVersion: number | null;
}

export interface PublicParticipantInfo {
  name: string | null;
  avatarUrl: string | null;
}

export interface PublicAttendeePreviewResponse {
  displayName: string | null;
  initials: string | null;
  avatarUrl: string | null;
}

export type PublicGroupDateStatus = "OPEN" | "FILLING_UP" | "FULLY_BOOKED" | "NO_SESSIONS" | "UNAVAILABLE";

export interface PublicGroupSessionCardResponse {
  sessionId: string;
  startTime: string;
  endTime: string;
  startLocalTime: string;
  endLocalTime: string;
  capacity: number;
  bookedCount: number;
  spotsLeft: number;
  occupancyPercent: number;
  bookable: boolean;
  additionalAttendeeCount: number;
  attendeePreview: PublicAttendeePreviewResponse[];
}

export interface PublicGroupDateCardResponse {
  date: string;
  status: PublicGroupDateStatus;
  sessionCount: number;
  bookableSessionCount: number;
  totalCapacity: number;
  totalBooked: number;
  nextAvailableStartTime: string | null;
  sessions: PublicGroupSessionCardResponse[];
}

export interface PublicGroupSeriesSummaryResponse {
  label: string;
  scheduleText: string;
  weekday: string | null;
  startDate: string | null;
  throughDate: string | null;
  firstSessionStartTime: string | null;
  lastSessionEndTime: string | null;
  sessionCountPerOccurrence: number;
}

export interface PublicGroupSessionsResponse {
  timezone: string;
  startDate: string;
  days: number;
  seriesSummary: PublicGroupSeriesSummaryResponse | null;
  dates: PublicGroupDateCardResponse[];
}

export interface PublicEventInfoResponse {
  name: string;
  duration: number;
  timezone: string;
  hostName: string;
  hostUsername: string;
  description: string;
  location: string;
  /**
   * The platform the call will be on ("GOOGLE_MEET", "ZOOM", …), already resolved from the host's
   * "use my default" pointer. Absent when it cannot be known yet — always on ROUND_ROBIN, whose
   * writer is the member assigned after the guest picks — so a missing value means "say nothing",
   * never "in person". Pair it with `location`, which carries free text only when the host chose
   * no video call at all.
   */
  conferencingProvider?: string | null;
  hostAvatarUrl?: string;
  /** Replaces the "BunnyCal" wordmark, or null to show ours. Not plan-gated. */
  brandName?: string | null;
  /** The host's brand colour (#rrggbb), or null. Not plan-gated — any host may set one. */
  brandColor?: string | null;
  /** Version-stamped public URL for the host's brand logo, or null. */
  brandLogoUrl?: string | null;
  /**
   * Server-composed CSS for the page surface: a hex colour or a `linear-gradient(...)` built from
   * validated parts. Never a string the host supplied. Null = the default page.
   */
  brandBackgroundCss?: string | null;
  /** A flat colour standing in for the surface (a gradient's midpoint), for contrast decisions. */
  brandBackgroundColor?: string | null;
  /**
   * Whether to show the BunnyCal mark. Resolved server-side from the host's stored preference
   * AND their current entitlement, so a lapsed plan restores it. Never decided client-side.
   */
  showBunnyCalBranding?: boolean;
  kind?: "ONE_ON_ONE" | "GROUP" | "ROUND_ROBIN" | "COLLECTIVE" | string;
  published?: boolean;
  /**
   * True only when the owner's current plan no longer includes this event's features — the host
   * must upgrade (or change the event) before it can take bookings. Independent of `published`:
   * a merely-unpublished event is `false` here. Carries no billing detail.
   */
  hostActionRequired?: boolean;
  participants?: PublicParticipantInfo[];
  /**
   * The team a multi-host event's pool came from. Absent whenever the pool was not drawn from
   * exactly one team, which is a normal case — always pair it with unnamed fallback wording.
   */
  teamName?: string | null;
  /** Weekdays the host works ("MONDAY"…"SUNDAY"). The calendar greys out the rest. */
  availableDays?: DayOfWeek[];
  /**
   * Specific dates ahead that are fully off — host day-offs and public holidays — as ISO dates
   * ("2026-12-25"). A weekday set cannot express these, so the calendar needs them to strike out a
   * holiday that falls on an otherwise-working weekday.
   */
  blockedDates?: string[];
  capacity?: number | null;
  recurrenceLabel?: string | null;
  seriesEndsAt?: string | null;
  paymentRequired?: boolean;
  paymentAmountMinor?: number | null;
  paymentCurrency?: string | null;
  paymentProvider?: string | null;
}

export interface SlotDto {
  slotId: string;
  start: string;
  end: string;
  available: boolean;
  sessionId?: string | null;
  capacity?: number | null;
  bookedCount?: number | null;
  spotsLeft?: number | null;
  attendees?: PublicParticipantInfo[];
  /** HMAC-signed token issued by the backend for Round Robin slot selection.
   *  Present only on RR event types. Must be forwarded verbatim in the hold request. */
  bookingToken?: string;
}

export type PublicAvailabilityStatus =
  | "AVAILABLE"
  | "NO_SLOTS_AVAILABLE"
  | "CALENDAR_NOT_CONNECTED"
  | "CALENDAR_SYNC_IN_PROGRESS"
  | "STALE_CALENDAR_DATA"
  | "NO_ELIGIBLE_PARTICIPANTS"
  | "OWNER_NOT_ENTITLED";

export interface SlotResponse {
  userId: string;
  eventTypeId: string;
  date: string;
  timezone: string;
  version: number;
  generatedAt: string;
  degraded: boolean;
  status?: PublicAvailabilityStatus | string;
  slots: SlotDto[];
}

/**
 * Minimal event-type context for the host reschedule dialog when the acting host is assigned to a
 * booking whose event type they do NOT own (a round-robin booking hosted by a participant, or one
 * handed off). Field names mirror EventTypeSummaryResponse where they overlap.
 */
export interface HostRescheduleContextResponse {
  id: string;
  name: string;
  slug: string;
  kind: string;
  durationMinutes: number;
  slotIntervalMinutes: number;
  ownerUsername: string | null;
  availableDays: DayOfWeek[];
  blockedDates: string[];
}

export interface PublicBookRequest {
  startTime: string;
  guestEmail: string;
  guestName: string;
  notes?: string;
  /** Must be present for Round Robin event types. Forwarded from SlotDto.bookingToken. */
  slotToken?: string;
  /** Embed-only: form answers collected by the embed widget. */
  answers?: AnswerInput[];
  /** Embed-only: signed token from GET /public/embed/:slug encodes experience+form versions. */
  embedToken?: string;
}

export interface HoldResponse {
  bookingId: string;
  expiresAt: string;
  status: BookingStatus;
  paymentRequired?: boolean;
  paymentAmountMinor?: number | null;
  paymentCurrency?: string | null;
  paymentProvider?: string | null;
}

export interface ConferenceDetailsResponse {
  provider: string;
  joinUrl: string | null;
  dialIn: string | null;
  meetingCode: string | null;
  password: string | null;
  sourceOfTruth: string;
}

export interface PublicConfirmResponse {
  bookingId: string;
  status: BookingStatus;
  manageToken?: string;
  provider?: string | null;
  externalEventId?: string | null;
  calendarSyncStatus?: string | null;
  providerEventUrl?: string | null;
  conferenceDetails?: ConferenceDetailsResponse | null;
  externalLifecycleState?: string | null;
  externalLifecycleReason?: string | null;
  reconcileSuppressed?: boolean | null;
  actionRequired?: boolean | null;
  /**
   * The round-robin participant the booking was assigned to at confirm time. The public event
   * response only carries the owner (the participant is not picked until confirm), so the
   * confirmation screen must prefer these when present. Null for the single-host kinds.
   */
  assignedHostName?: string | null;
  assignedHostAvatarUrl?: string | null;
}

export interface EventTypeSummaryResponse {
  id: string;
  name: string;
  slug: string;
  link: string;
  kind?: "ONE_ON_ONE" | "GROUP" | "ROUND_ROBIN" | "COLLECTIVE" | string;
  capacity?: number | null;
  groupHostNotificationMode?: GroupHostNotificationMode | string;
  durationMinutes?: number | null;
  published?: boolean;
  degraded?: boolean;
  seriesStartDate?: string | null;
  seriesEndDate?: string | null;
  conference?: EventTypeConferenceConfigResponse | null;
  /** INHERIT follows the event owner's global hours; CUSTOM uses host-defined event hours. */
  availabilityMode?: EventAvailabilityMode | string;
  availabilityWindows?: EventAvailabilityWindowSummary[];
  description?: string | null;
  location?: string | null;
  bufferBeforeMinutes?: number | null;
  bufferAfterMinutes?: number | null;
  slotIntervalMinutes?: number | null;
  minNoticeMinutes?: number | null;
  maxAdvanceDays?: number | null;
  holdDurationMinutes?: number | null;
  payment?: EventPaymentConfigResponse | null;
}

export interface EventPaymentConfigRequest {
  enabled: boolean;
  connectionId?: string;
  amountMinor?: number;
  currency?: string;
}

export interface EventPaymentConfigResponse {
  enabled: boolean;
  connectionId: string;
  amountMinor: number;
  currency: string;
  provider: string;
}

export interface PaymentConnectionResponse {
  id: string;
  provider: "STRIPE" | "PAYPAL" | string;
  status: "ONBOARDING" | "READY" | "RESTRICTED" | "DISCONNECTED" | string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  restrictionReason?: string | null;
}

export interface PaymentInitializationResponse {
  paymentId: string;
  provider: string;
  providerAccountId: string;
  publishableKey?: string | null;
  clientSecret?: string | null;
  approvalUrl?: string | null;
  amountMinor: number;
  currency: string;
  status: string;
  expiresAt: string;
}

export interface PublicPaymentStatusResponse {
  paymentId: string;
  paymentStatus: string;
  bookingStatus: string;
  confirmed: boolean;
  refunded: boolean;
}

export interface HostBookingPaymentResponse {
  paymentId: string;
  provider: string;
  amountMinor: number;
  currency: string;
  status: string;
  paidAt?: string | null;
  refundedAt?: string | null;
  failureCode?: string | null;
  requiresAttention: boolean;
}

export type GroupHostNotificationMode =
  | "SMART_SUMMARY"
  | "EVERY_REGISTRATION"
  | "DAILY_DIGEST"
  | "IMPORTANT_ONLY"
  | "NONE";

export type EventAvailabilityMode = "INHERIT" | "CUSTOM";

export interface EventAvailabilityWindowSummary {
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
}

export interface EventAvailabilityScheduleRequest {
  mode: EventAvailabilityMode;
  windows: RecurringWindowRequest[];
}

export interface EventAvailabilityScheduleResponse {
  mode: EventAvailabilityMode;
  windows: Array<{
    id: string;
    eventTypeId: string;
    dayOfWeek: DayOfWeek;
    startTime: string;
    endTime: string;
  }>;
}

export interface CalendarEventEntry {
  id: string;
  title: string | null;
  start: string;
  end: string;
  sourceId: string;
  /** Display name of the calendar this came from, falling back to the account email. */
  sourceName: string | null;
  provider: string;
  status: string;
  externalEventId: string | null;
  externalCalendarId: string | null;
  location: string | null;
  organizerEmail: string | null;
  /** Null when the event has no video call, and on rows synced before links were captured. */
  conferenceUrl: string | null;
  providerEventUrl: string | null;
  /** Other people on the event, excluding the connected account. */
  attendees: string[];
  allDay: boolean;
}

export interface CalendarEventsResponse {
  events: CalendarEventEntry[];
}

export interface CalendarHolidayEntry {
  id: string;
  title: string;
  date: string;
}

export interface CalendarHolidaysResponse {
  holidays: CalendarHolidayEntry[];
}

export interface MeetingSummaryResponse {
  bookingId: string;
  eventTypeId?: string | null;
  eventTypeKind?: string | null;
  eventTypeName: string;
  startTime: string;
  endTime: string;
  bookingStatus: string;
  guestName: string;
  guestEmail: string;
  provider?: string | null;
  calendarSyncStatus?: string | null;
  externalEventId?: string | null;
  providerEventUrl?: string | null;
  conferenceDetails?: ConferenceDetailsResponse | null;
  externalLifecycleState?: string | null;
  externalLifecycleReason?: string | null;
  reconcileSuppressed?: boolean | null;
  actionRequired?: boolean | null;
}

export interface QuestionnaireResponse {
  questionLabel: string;
  answerValue: string;
}

export interface BookingDetailResponse extends MeetingSummaryResponse {
  notes?: string | null;
  questionnaireResponses?: QuestionnaireResponse[];
  payment?: HostBookingPaymentResponse | null;
  /** Additional invite-only guests the booker attached. */
  guests?: string[];
}

export interface SessionSyncResponse {
  syncStatus?: string;
  /** The provider's id for this session's calendar event. Always sent by the API; declared
   *  here so the availability grid can recognise the provider's own copy of a session and
   *  avoid drawing it a second time as an unrelated busy block. */
  externalEventId?: string | null;
  providerEventUrl?: string | null;
  conferenceUrl?: string | null;
  conferenceProvider?: string | null;
}

export interface SessionSummaryResponse {
  sessionId: string;
  eventTypeId: string;
  eventTypeName: string;
  eventTypeSlug?: string;
  startTime: string;
  endTime: string;
  /**
   * Where the recurrence rule originally placed this occurrence, or null when no rule produced it.
   * Differs from startTime exactly when the host has moved the session — which is how the
   * availability grid knows to keep showing the vacated hour as reserved.
   */
  scheduledOccurrenceStart?: string | null;
  status: string;
  capacity: number;
  confirmedCount: number;
  pendingCount: number;
  registrationCount?: number;
  cancelledCount?: number;
  occupancyPercent?: number;
  past?: boolean;
  sync?: SessionSyncResponse | null;
  [key: string]: unknown;
}

export interface SessionRegistrationResponse {
  registrationId: string;
  guestName: string;
  guestEmail: string;
  notes?: string | null;
  questionnaireResponses?: QuestionnaireResponse[];
  status: string;
  createdAt?: string;
  expiresAt?: string | null;
  payment?: HostBookingPaymentResponse | null;
  [key: string]: unknown;
}

export interface SessionRegistrationPageResponse {
  items: SessionRegistrationResponse[];
  nextCursor?: string | null;
  hasMore?: boolean;
}

export interface CreateEventTypeRequest {
  name: string;
  description: string;
  location: string;
  durationMinutes: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  slotIntervalMinutes: number;
  minNoticeMinutes: number;
  maxAdvanceDays: number;
  holdDurationMinutes: number;
  slug: string;
  kind?: "ONE_ON_ONE" | "GROUP" | "ROUND_ROBIN" | "COLLECTIVE" | string;
  capacity?: number;
  groupHostNotificationMode?: GroupHostNotificationMode;
  conference?: EventTypeConferenceConfigRequest;
  payment?: EventPaymentConfigRequest;
}

/**
 * Partial update. An omitted field leaves the stored value alone.
 *
 * `kind` is absent deliberately: it gates entitlements at creation and is immutable.
 *
 * Calendars are no longer per-event: which calendars block you and which one receives your bookings
 * are per-user settings on the Integrations page.
 */
export interface UpdateEventTypeRequest {
  name?: string;
  description?: string;
  location?: string;
  durationMinutes?: number;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  slotIntervalMinutes?: number;
  minNoticeMinutes?: number;
  maxAdvanceDays?: number;
  holdDurationMinutes?: number;
  capacity?: number;
  groupHostNotificationMode?: GroupHostNotificationMode;
  conference?: EventTypeConferenceConfigRequest;
  payment?: EventPaymentConfigRequest;
}

export interface EventTypeConferenceConfigRequest {
  enabled?: boolean;
  provider?: string;
  customUrl?: string;
}

export interface EventTypeConferenceConfigResponse {
  enabled: boolean;
  provider: string;
  customUrl?: string;
}

export type DayOfWeek =
  | "MONDAY"
  | "TUESDAY"
  | "WEDNESDAY"
  | "THURSDAY"
  | "FRIDAY"
  | "SATURDAY"
  | "SUNDAY";

export type ScheduleType = "ONE_TIME" | "RECURRING";
export type RecurrenceFrequency = "WEEKLY";
export type RecurrenceEndMode = "NONE" | "UNTIL_DATE" | "OCCURRENCE_COUNT";

export interface AvailabilityRuleRequest {
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
}

export interface BulkAvailabilityRulesUpsertRequest {
  rules: AvailabilityRuleRequest[];
}

// Event-scoped scheduling window shapes used by the reservation-windows and
// availability-windows endpoints. Both accept a bare JSON array.
// For GROUP reservation windows, scheduleType controls ONE_TIME vs RECURRING.
// Legacy callers that omit scheduleType default to RECURRING/WEEKLY/NONE on the backend.
export interface RecurringWindowRequest {
  // Existing window to update in place; omit to create a new one. Always round-trip
  // this from the response — sessions link to it, and a payload where every window
  // omits an id is treated by the server as a legacy replace-all.
  id?: string;
  // Schedule type — omit for legacy RECURRING weekly behaviour
  scheduleType?: ScheduleType;
  startTime: string;
  endTime: string;
  // RECURRING fields
  dayOfWeek?: DayOfWeek;
  frequency?: RecurrenceFrequency;
  startDate?: string;           // ISO date "YYYY-MM-DD"
  recurrenceEndMode?: RecurrenceEndMode;
  untilDate?: string;           // ISO date, required when recurrenceEndMode = "UNTIL_DATE"
  occurrenceCount?: number;     // required when recurrenceEndMode = "OCCURRENCE_COUNT"
  // ONE_TIME fields
  eventDate?: string;           // ISO date, required when scheduleType = "ONE_TIME"
}

/**
 * A booked session that stopped following its recurrence rule.
 *
 * `startTime` is where it actually sits; `scheduledOccurrenceStart` is where the rule
 * originally placed it. Showing both is what makes the divergence legible to the host.
 */
export interface PinnedSessionResponse {
  sessionId: string;
  startTime: string;
  endTime: string;
  scheduledOccurrenceStart: string | null;
  confirmedCount: number;
  capacity: number;
  detachedReason: string | null;
  detachedAt: string | null;
}

export interface RescheduleConflictItem {
  title: string;
  startTime: string;
  endTime: string;
  /** GROUP_SESSION | BOOKING | EXTERNAL_CALENDAR */
  source: string;
}

/**
 * Preview of what a proposed reschedule time collides with.
 *
 * `blocked` and `requiresConfirmation` are computed server-side rather than derived from the
 * list contents, so the rule for what counts as blocking lives in one place.
 */
export interface RescheduleConflictResponse {
  blocked: boolean;
  requiresConfirmation: boolean;
  hardConflicts: RescheduleConflictItem[];
  softConflicts: RescheduleConflictItem[];
}

export interface SeriesOperationResponse {
  affectedCount: number;
  message: string;
}

export interface RecurringWindowResponse {
  id: string;
  eventTypeId: string;
  scheduleType: ScheduleType;
  startTime: string;
  endTime: string;
  // RECURRING fields (present when scheduleType = "RECURRING")
  dayOfWeek?: DayOfWeek | null;
  frequency?: RecurrenceFrequency | null;
  startDate?: string | null;
  recurrenceEndMode?: RecurrenceEndMode | null;
  untilDate?: string | null;
  occurrenceCount?: number | null;
  // ONE_TIME fields (present when scheduleType = "ONE_TIME")
  eventDate?: string | null;
}

export interface AvailabilityOverrideCreateRequest {
  date: string;
  startTime?: string;
  endTime?: string;
  available: boolean;
  isAvailable?: boolean;
  label?: string;
}

export interface AvailabilityRuleResponse {
  id: string;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
}

export interface GroupReservationBlockerResponse {
  windowId: string;
  eventTypeId: string;
  eventTypeName: string;
  startTime: string;
  endTime: string;
  // Recurrence fields from the backend
  scheduleType?: ScheduleType;
  dayOfWeek?: DayOfWeek | null;
  eventDate?: string | null;
  startDate?: string | null;
  recurrenceEndMode?: RecurrenceEndMode | null;
  untilDate?: string | null;
  occurrenceCount?: number | null;
}

export interface AvailabilityOverrideResponse {
  id: string;
  date: string;
  startTime?: string | null;
  endTime?: string | null;
  isAvailable?: boolean;
  available?: boolean;
  label?: string | null;
}

export interface RefreshRequest {
  refreshToken: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: UserDto;
}

export interface SessionContextResponse {
  userId?: string;
  linkedProviders?: string[];
  activeAuthProvider?: string;
  organizationHints?: string[];
  authFreshness?: string;
  onboardingState?: string;
}

export interface AuthProviderOptionResponse {
  provider?: string;
  providerId?: string;
  displayName?: string;
  authorizationPath?: string;
  supportsOAuth?: boolean;
  loginUrl?: string;
  label?: string;
  enabled?: boolean;
}

export interface AuthOnboardingResponse {
  providers?: AuthProviderOptionResponse[];
  linkedIdentityProviders?: string[];
}

export interface LinkProviderResponse {
  authorizationUrl?: string;
  expiresAt?: string;
}

export interface AccountDeletionResponse {
  accepted: boolean;
  existingJob: boolean;
}

export interface LogoutRequest {
  userId: string;
}

export interface CalendarStatusMap {
  [provider: string]: string;
}

export type IntegrationKind = "calendar" | "conferencing";

export interface ProviderStatusEntry {
  status: string;
  // Optional provider-aware metadata (calendar list, last sync, etc).
  calendars?: ProviderCalendarSummary[];
  [key: string]: unknown;
}

export interface ProviderCalendarSummary {
  id: string;
  name?: string;
  primary?: boolean;
  selected?: boolean;
  // Backends may return additional metadata.
  [key: string]: unknown;
}

export interface ProviderAwareStatusMap {
  [provider: string]: ProviderStatusEntry;
}

// Capability metadata exposed by /integrations/{domain}/status* endpoints.
// Keys mirror the backend enum casing (GOOGLE, MICROSOFT, GOOGLE_MEET, ZOOM, CUSTOM_URL, NONE).
export interface ProviderCapabilityFlags {
  supportsWebhooks?: boolean;
  supportsConferencing?: boolean;
  supportsAvailabilitySync?: boolean;
  supportsPushRenewal?: boolean;
  supportsMultipleCalendars?: boolean;
  supportsNativeTeams?: boolean;
  accountType?: "PERSONAL_MSA" | "MICROSOFT_365" | string;
  [key: string]: unknown;
}

export interface ProviderCapabilityMap {
  [providerEnum: string]: ProviderCapabilityFlags;
}

export interface CalendarConnectionRuntime {
  connectionId: string;
  provider: string;
  displayName: string;
  email: string;
  account?: {
    type?: string;
    supportsNativeTeams?: boolean;
    [key: string]: unknown;
  } | null;
  status: string;
  actionRequired: boolean;
  /**
   * True on the connection that receives ALL of this user's confirmed bookings — their own event
   * types and any team event they are booked on. At most one per user (enforced server-side).
   */
  defaultWriteback?: boolean;
  capabilities: {
    availability: boolean;
    projection: boolean;
    conferencingProvisioning: boolean;
    webhooks: boolean;
  };
  roles: {
    availabilityEligible: boolean;
    projectionEligible: boolean;
    conferencingEligible: boolean;
  };
  externalCalendarId: string;
  calendars: Array<{
    calendarId: string;
    name: string;
    isPrimary: boolean;
    canRead: boolean;
    canWrite: boolean;
    /**
     * This calendar is checked for conflicts. It applies everywhere the user can be booked — their
     * own event types AND any team event (round-robin/collective) they are a member of — not just
     * to one event type. Default on.
     */
    selectedForAvailability: boolean;
    /**
     * This is the one calendar within its connection that receives bookings. Only meaningful on the
     * connection carrying `defaultWriteback`; on any other connection it is a remembered preference.
     */
    selectedForProjection: boolean;
  }>;
}

export interface ConferencingRuntimeState {
  zoomConnected: boolean;
  /** "Google Meet can be the resolved DEFAULT" — i.e. the write-back calendar is a Google one. */
  googleMeetAvailable: boolean;
  /** "Teams can be the resolved DEFAULT" — i.e. the write-back calendar is a Microsoft 365 one. */
  teamsAvailable: boolean;
  /** What "My default" currently resolves to: "google_meet" | "microsoft_teams" | "zoom" | "none". */
  defaultProvider: string;
}

export interface PublicRescheduleRequest {
  startTime: string;
  /**
   * Required for COLLECTIVE, where the server re-checks the move against every host and needs
   * proof the slot came from the current roster's intersection. Forwarded from SlotDto.bookingToken.
   */
  slotToken?: string;
}

export interface PublicManageBookingResponse {
  bookingId: string;
  eventTitle: string;
  eventKind: "ONE_ON_ONE" | "GROUP" | "ROUND_ROBIN" | "COLLECTIVE" | string;
  durationMinutes: number;
  startTime: string;
  endTime: string;
  hostName: string;
  hostUsername: string;
  hostAvatarUrl?: string | null;
  /** Host branding, resolved from the booking itself — so a copied link needs no browser state. */
  brandName?: string | null;
  brandColor?: string | null;
  brandLogoUrl?: string | null;
  brandBackgroundCss?: string | null;
  brandBackgroundColor?: string | null;
  showBunnyCalBranding?: boolean;
  attendeeName: string;
  attendeeEmail: string;
  conferenceDetails?: ConferenceDetailsResponse | null;
  notes?: string | null;
  questionnaireResponses?: QuestionnaireResponse[];
  /** Additional guests the booker attached, echoed back so they see their own list. */
  guests?: string[];
  status: BookingStatus | string;
  externalLifecycleState?: string | null;
  externalLifecycleReason?: string | null;
  timezone?: string | null;
}

export interface PublicBookingStatusResponse {
  bookingId: string;
  status: BookingStatus | string;
  startTime?: string | null;
  endTime?: string | null;
  expiresAt?: string | null;
}

export type TeamRole = "OWNER" | "ADMIN" | "MEMBER";
export type InvitationStatus = "PENDING" | "ACCEPTED" | "DECLINED" | "EXPIRED" | "REVOKED";

export interface TeamResponse {
  id: string;
  ownerUserId: string;
  name: string;
  slug: string;
  memberCount: number;
}

export interface TeamDeletionImpactResponse {
  memberCount: number;
  pendingInvitationCount: number;
}

export interface TeamMemberResponse {
  id: string;
  teamId: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  userProfileImageUrl: string | null;
  role: TeamRole;
  joinedAt: string;
}

export interface TeamInvitationResponse {
  id: string;
  teamId: string;
  invitedEmail: string;
  role: TeamRole;
  status: InvitationStatus;
  token: string;
  expiresAt: string;
  createdAt: string;
}

export type ParticipantEligibilityReason =
  | "ACTIVE"
  | "USER_INACTIVE"
  | "USER_DELETED"
  | "USER_NOT_FOUND"
  | "NO_AVAILABILITY_RULES"
  | "NO_ACTIVE_CALENDAR";

export type ParticipantReadinessStatus =
  | "READY"
  | "NO_AVAILABILITY"
  | "NO_CALENDAR"
  | "NO_WRITEBACK"
  | "DEGRADED_CALENDAR"
  | "INACTIVE"
  | "REVOKED"
  | "NOT_SCHEDULABLE";

export interface EventTypeParticipantResponse {
  userId: string;
  userName: string | null;
  userEmail: string | null;
  userProfileImageUrl: string | null;
  displayOrder: number;
  isOwner: boolean;
  inTeam: boolean;
  eligible: boolean;
  eligibilityReason: ParticipantEligibilityReason;
  hasAvailabilityRules: boolean;
  hasActiveCalendar: boolean;
  calendarProvider: string | null;
  hasWritebackCapability: boolean;
  readinessStatus: ParticipantReadinessStatus;
  readinessMessage?: string | null;
  supportsNativeTeams?: boolean;
  /**
   * Whether this participant can create the meeting link this event needs, from their own account.
   * Round Robin skips those who cannot, so they receive no bookings. Separate from
   * `readinessStatus`, which stays READY for them — they are set up in every other sense.
   *
   * Always true for non-round-robin kinds: every other kind mints the link from the owner, so a
   * participant is an attendee on someone else's meeting and never its creator.
   */
  canCreateMeetingLink?: boolean;
  /**
   * The provider this participant would actually use ("ZOOM", "GOOGLE_MEET", "MICROSOFT_TEAMS").
   * Differs per member when the event uses "My default". Null when there is no link to mint.
   */
  resolvedConferencingProvider?: string | null;
}

/**
 * Aggregate meeting-link coverage for a proposed round-robin roster.
 *
 * Counts only, never per-member detail: the host is choosing a provider for an event, not auditing
 * colleagues, so a total is all the decision needs.
 */
export interface ConferencingCoverageResponse {
  totalParticipants: number;
  zoomCapableCount: number;
  defaultCapableCount: number;
}

export interface CreateTeamRequest {
  name: string;
  slug?: string;
}

export interface InviteMemberRequest {
  email: string;
  role?: TeamRole;
}

export interface BulkInviteMembersRequest {
  emails: string[];
  role?: TeamRole;
}

export interface FailedInvite {
  email: string;
  /** Machine-readable ErrorCode name, e.g. "TEAM_INVITATION_ALREADY_PENDING". */
  code: string;
  reason: string;
}

export interface BulkInviteMembersResponse {
  sent: TeamInvitationResponse[];
  failed: FailedInvite[];
}

export type SetupRequestStatus = "NOT_STARTED" | "REQUESTED" | "COMPLETED";

export interface SetupStatusResponse {
  status: SetupRequestStatus;
  requestedAt: string | null;
  lastRemindedAt: string | null;
  canResend: boolean;
}

export interface MemberReadinessEntry {
  userId: string;
  userName: string | null;
  userEmail: string | null;
  userProfileImageUrl: string | null;
  readinessStatus: ParticipantReadinessStatus;
  hasAvailabilityRules: boolean;
  hasActiveCalendar: boolean;
  teamMemberId: string;
}

export interface TeamReadinessSummaryResponse {
  totalMembers: number;
  ready: number;
  needsSetup: number;
  members: MemberReadinessEntry[];
}

export interface ParticipantAssignmentStat {
  userId: string;
  userName: string | null;
  userEmail: string | null;
  readinessStatus: ParticipantReadinessStatus;
  bookingCount: number;
  lastAssignedAt: string | null;
}

export interface RoundRobinStatsResponse {
  totalParticipants: number;
  ready: number;
  needsSetup: number;
  assignmentDistribution: ParticipantAssignmentStat[];
}

export interface PublishReadinessResponse {
  published: boolean;
  publishable: boolean;
  degraded: boolean;
  reasons: string[];
  totalParticipants: number;
  readyCount: number;
  participants: EventTypeParticipantResponse[];
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

export class ApiError extends Error {
  constructor(public code: string, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

// ─── Embed / Form types ────────────────────────────────────────────────────

export type QuestionType =
  | "SHORT_TEXT" | "LONG_TEXT" | "EMAIL" | "PHONE" | "URL" | "NUMBER"
  | "SINGLE_SELECT" | "MULTI_SELECT" | "BOOLEAN" | "DATE";

export interface FormQuestionOptionResponse {
  id: string;
  label: string;
  value: string;
  sortOrder: number;
}

export interface FormQuestionResponse {
  id: string;
  questionText: string;
  questionType: QuestionType;
  required: boolean;
  sortOrder: number;
  options: FormQuestionOptionResponse[];
}

export interface FormResponse {
  id: string;
  name: string;
  description: string | null;
  version: number;
  questions: FormQuestionResponse[];
}

/** How an embedded widget paints its surface. Structured, never raw CSS. */
export type BackgroundType = "SOLID" | "LINEAR_GRADIENT";

export interface BookingExperienceResponse {
  id: string;
  name: string;
  slug: string;
  eventTypeId: string;
  formId: string | null;
  primaryColor: string | null;
  /** Replaces the "BunnyCal" wordmark in this widget's lockup; null falls back to the owner's
   * account-level brand name, then to "BunnyCal". */
  brandName: string | null;
  backgroundType: BackgroundType;
  backgroundColor: string | null;
  gradientStartColor: string | null;
  gradientEndColor: string | null;
  gradientAngle: number | null;
  textColor: string | null;
  showBranding: boolean;
  status: "DRAFT" | "ACTIVE" | "ARCHIVED";
  version: number;
  /** Version-stamped public URL for the host's logo, or null when none is uploaded. */
  logoUrl: string | null;
  /**
   * Whether the owner's plan currently permits removing the BunnyCal mark. Lets the builder
   * disable the toggle without a second entitlements call; the public embed read is the authority.
   */
  brandingRemovable: boolean;
}

export interface PublicEmbedConfigResponse {
  eventInfo: PublicEventInfoResponse;
  form: FormQuestionResponse[] | null;
  primaryColor: string | null;
  /**
   * Ready-to-use CSS for the widget surface — a hex color, or a linear-gradient() the server
   * generated from validated parts. The widget never assembles this string itself.
   */
  backgroundCss: string | null;
  /** Flat color approximating the surface (a gradient's midpoint), used for contrast math. */
  backgroundColor: string | null;
  textColor: string | null;
  /** Resolved server-side against the owner's current plan — never trust a client override. */
  showBranding: boolean;
  embedToken: string;
  eventTypeSlug: string;
  /** Version-stamped public URL for the host's logo, or null when none is uploaded. */
  logoUrl: string | null;
  /** Replaces the "BunnyCal" wordmark in the widget's lockup; null means show "BunnyCal". */
  brandName: string | null;
}

export interface AnswerInput {
  questionId: string;
  answerText?: string;
  answerOptions?: string[];
}
